import { useMsal } from '@azure/msal-react'
import {
  BROWSE_CREDIT_APPLICATIONS_FILTERS_ENABLED_KEY,
  BROWSE_CREDIT_APPLICATIONS_FILTERS_KEY,
  CREDIT_TASK_DASHBOARD_FILTERS_ENABLED_KEY,
  CREDIT_TASK_DASHBOARD_FILTERS_KEY,
  LOGIN_PREFERENCE_KEY,
  PREFERS_DARK_SETTING,
  SELECTED_COMPANY_LOCAL_STORAGE,
} from '@src/contexts/preferences-context'
import { JWT_LOCAL_STORAGE_KEY } from '@src/contexts/user-context'
import { getApiClient } from '@src/services/api-client'
import { reportErrorToConsole } from '@src/services/error-logger'
import { MutationFunction, useMutation } from '@tanstack/react-query'
import { AxiosRequestConfig } from 'axios'
import { useCallback } from 'react'
import { LoginCredentials } from '../../types/LoginCredentials'

export interface JwtToken {
  jwt: string
}

/**
 * Clears localStorage and reloads the page.
 * Used for 401 responses and session expiration to trigger UserProvider auth flow.
 */
function clearLocalStoragePreservingPreferences(): void {
  const keysToPreserve = [
    PREFERS_DARK_SETTING,
    BROWSE_CREDIT_APPLICATIONS_FILTERS_KEY,
    BROWSE_CREDIT_APPLICATIONS_FILTERS_ENABLED_KEY,
    CREDIT_TASK_DASHBOARD_FILTERS_KEY,
    CREDIT_TASK_DASHBOARD_FILTERS_ENABLED_KEY,
    SELECTED_COMPANY_LOCAL_STORAGE,
    LOGIN_PREFERENCE_KEY,
  ]

  const preserved = new Map<string, string>()
  for (const key of keysToPreserve) {
    const value = localStorage.getItem(key)
    if (value !== null) preserved.set(key, value)
  }

  localStorage.clear()

  for (const [key, value] of preserved) {
    localStorage.setItem(key, value)
  }
}

export function clearSessionAndReload(): void {
  clearLocalStoragePreservingPreferences()
  window.location.reload()
}

// Standalone function for UserProvider
export const signIn = async (data: JwtToken): Promise<JwtToken> => {
  const apiClient = getApiClient()
  const payload: AxiosRequestConfig = {
    method: 'POST',
    baseURL: import.meta.env.VITE_BACKOFFICE_API_URL,
    url: '/GenerateJwtToken',
    data,
    withCredentials: true,
  }
  const response = await apiClient.request(payload)
  return response.data as JwtToken
}

// Mock login for development/testing purposes
const mockLogin: MutationFunction<JwtToken, LoginCredentials> = async (data) => {
  const apiClient = getApiClient()
  const payload: AxiosRequestConfig = {
    method: 'POST',
    baseURL: import.meta.env.VITE_BACKOFFICE_API_URL,
    url: '/MockLogin',
    data,
    withCredentials: true,
  }
  const response = await apiClient.request(payload)
  return response.data as JwtToken
}

/**
 * Hook for mock login (development/testing only).
 * Stores JWT in localStorage and reloads the page to trigger UserProvider.
 */
export function useMockLogin() {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: mockLogin,
    onSuccess: (data) => {
      localStorage.setItem(JWT_LOCAL_STORAGE_KEY, data.jwt)
      window.location.reload()
    },
  })

  return [mutateAsync, isPending] as const
}

/**
 * Hook for logout.
 * Clears localStorage and triggers MSAL logout redirect.
 */
export function useLogout(): () => void {
  const { instance, accounts } = useMsal()
  const account = accounts[0] ?? null

  return useCallback(() => {
    clearLocalStoragePreservingPreferences()
    if (account) {
      instance.logoutRedirect({ account }).catch(reportErrorToConsole)
    } else {
      clearSessionAndReload()
    }
  }, [instance, account])
}
